/**
 * « Automatismes » — une série de flash, entière et chronométrée (`exercise` §10).
 *
 * **L'écran qui manquait pour que le flash existe.** Jusqu'au 10 septembre 2026
 * un flash se servait à l'unité comme n'importe quel exercice, et c'est tout ce
 * que les deux enfants ont jamais reçu : 100 % de leurs tentatives. Servi seul,
 * il ne reste de lui qu'un item court et facile présenté comme « l'exercice du
 * soir » — le §10 demande une **série** sur une même compétence (de un à dix
 * items), un **chronomètre annoncé et visible**, et une difficulté calée sur
 * `p = 0,9`. Le tirage du soir ne sait offrir aucun des trois ; il ne sert donc
 * plus cette forme, et l'écran du soir envoie ici (`Competence.serie`).
 *
 * Trois choses qui ne se font nulle part ailleurs dans la coquille élève :
 *
 * 1. **Un chronomètre se voit.** `ExercisePanel` mesure depuis le 30 août et
 *    n'affiche rien, pour une raison écrite dans son en-tête : un compteur
 *    visible transformerait chaque exercice en épreuve chronométrée. Le §10 est
 *    l'exception, et elle tient parce que le chronomètre est **annoncé avant de
 *    partir** — c'est la règle du jeu, pas un piège découvert en route — et
 *    parce qu'il est **au niveau de la série**, jamais sur une question : une
 *    fluence se mesure sur une suite, et un compteur par item redevient un
 *    verdict sur chaque réponse.
 * 2. **Il monte.** Aucune durée à battre, aucun compte à rebours ; la réponse du
 *    serveur ne porte d'ailleurs pas de total (`SerieFlash`), et c'est délibéré.
 * 3. **La série est entière et déterministe.** Les items arrivent tous ensemble
 *    et dans un ordre stable : recharger l'écran doit rendre la même série au
 *    même rang, sans quoi l'enfant qui revient sur un chronomètre qui tourne
 *    croirait avoir déjà fait ce qu'il n'a pas fait.
 *
 * **Aucun score, ici comme ailleurs** (invariant §14.1). Ce qui s'affiche est un
 * rang dans une file — « Question 3 sur 8 » — et une durée écoulée. Ni bonnes
 * réponses, ni moyenne, ni comparaison avec la dernière fois : c'est la
 * comparaison, et non le chiffre, qui referait un test de rapidité.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { call } from "@/api/client";
import type { SerieFlash } from "@/api/schema";
import { ExerciceEtCorrige } from "@/child/ExerciceEtCorrige";
import type { Chrono } from "@/child/chrono";
import { demarrer, releve, visibilite } from "@/child/chrono";
import { annonce, avancement, bilan, chronoEnClair, combienDeQuestions } from "@/child/serie";
import { etiquette } from "@/ui/matieres";
import { Button, Card, ErrorNote, Loading, Screen } from "@/ui/primitives";

type Etat =
  | { quoi: "chargement" }
  | { quoi: "panne"; message: string }
  // **Pas assez d'exercices n'est pas une panne.** Il faut au moins un item
  // flash dans la plage, et la nuit de remplissage est suspendue depuis le
  // 28 août 2026. C'est un état probable, et l'afficher en rouge apprendrait à
  // Baya qu'elle a cassé quelque chose.
  | { quoi: "pas_assez" }
  // L'annonce, avant que quoi que ce soit ne parte. C'est cet état qui rend le
  // chronomètre acceptable ; le supprimer pour gagner un clic le rendrait
  // furtif.
  | { quoi: "prete"; serie: SerieFlash }
  | { quoi: "encours"; serie: SerieFlash; index: number }
  | { quoi: "finie"; serie: SerieFlash; ms: number };

export function SerieFlashScreen() {
  const { competencyId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [etat, setEtat] = useState<Etat>({ quoi: "chargement" });

  // **Le chronomètre vit dans une référence, l'affichage dans un état.** Ce sont
  // deux choses : la mesure est continue, le rendu est à la seconde. Faire vivre
  // la mesure dans `useState` provoquerait un rendu par pas d'horloge, et la
  // remonter dans le rendu la ferait repartir de zéro à chaque question.
  const chrono = useRef<Chrono>(demarrer(0));
  const [ms, setMs] = useState(0);

  // D'où l'on vient. Le travail du soir et la révision mènent tous deux ici, et
  // `navigate(-1)` sortirait de l'application quand l'URL est ouverte
  // directement — dans une PWA installée, il n'y a pas de bouton « précédent »
  // pour rattraper ça.
  const retour = (location.state as { retour?: string } | null)?.retour ?? "/eleve/travail";

  const charger = useCallback(async () => {
    setEtat({ quoi: "chargement" });
    try {
      const serie = await call("serieFlash", { body: { competency_id: competencyId } });
      setEtat({ quoi: "prete", serie });
    } catch {
      // La route rend 404 quand le stock ne suffit pas à une série, et c'est de
      // loin le cas le plus fréquent ; on ne distingue pas les deux ici, parce
      // que la seule chose que l'enfant puisse faire est la même — aller
      // travailler autre chose.
      setEtat({ quoi: "pas_assez" });
    }
  }, [competencyId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  // **L'horloge s'arrête quand l'écran passe en arrière-plan**, comme celle des
  // exercices : une tablette posée pendant le dîner ajouterait quarante minutes
  // à une série de trois, et c'est le seul chiffre de cet écran.
  useEffect(() => {
    if (etat.quoi !== "encours") return;
    const battre = () => setMs(releve(chrono.current, performance.now()).ms);
    const bascule = () => {
      chrono.current = visibilite(
        chrono.current,
        document.visibilityState === "visible",
        performance.now(),
      );
      battre();
    };
    battre();
    const horloge = window.setInterval(battre, 500);
    document.addEventListener("visibilitychange", bascule);
    return () => {
      window.clearInterval(horloge);
      document.removeEventListener("visibilitychange", bascule);
    };
  }, [etat.quoi]);

  if (etat.quoi === "chargement") return <Loading label="Je prépare la série…" />;

  if (etat.quoi === "panne") {
    return (
      <Screen title="Automatismes">
        <ErrorNote>{etat.message}</ErrorNote>
        <Button onClick={() => navigate(retour)}>← Revenir au travail</Button>
      </Screen>
    );
  }

  if (etat.quoi === "pas_assez") {
    return (
      <Screen title="Automatismes">
        <Card title="Pas encore de série sur ce sujet">
          <p>
            Il n'y a pas encore assez de questions pour faire une série ici. Ce n'est pas
            de ta faute et il n'y a rien à réparer : choisis autre chose pour ce soir.
          </p>
        </Card>
        <Button onClick={() => navigate(retour)}>← Revenir au travail</Button>
      </Screen>
    );
  }

  // La matière est facultative au contrat — un référentiel indisponible la
  // laisse vide — et elle sert à deux choses qui doivent survivre à son absence :
  // titrer l'écran, et décider si la saisie ouvre le clavier de formules.
  const matiere = etat.serie.matiere ?? "";
  const titre = matiere ? etiquette(matiere) : "Automatismes";

  if (etat.quoi === "prete") {
    return (
      <Screen title={titre}>
        <Card title={etat.serie.libelle}>
          <p className="lede">{annonce(etat.serie.items.length)}</p>
          <p className="serie__combien">{combienDeQuestions(etat.serie.items.length)}</p>
          <div className="revision__suite">
            <Button
              onClick={() => {
                chrono.current = demarrer(performance.now());
                setMs(0);
                setEtat({ quoi: "encours", serie: etat.serie, index: 0 });
              }}
            >
              C'est parti
            </Button>
            <Button variant="quiet" onClick={() => navigate(retour)}>
              Pas maintenant
            </Button>
          </div>
        </Card>
      </Screen>
    );
  }

  if (etat.quoi === "finie") {
    return (
      <Screen title={titre}>
        <Card title="Série finie">
          {/* La phrase vit dans `child/serie` : elle porte la règle du §10 et
              elle s'accorde au nombre d'items, un item étant une série depuis
              le 15 septembre 2026. */}
          <p>{bilan(etat.serie.items.length, etat.ms)}</p>
          <div className="revision__suite">
            <Button onClick={() => navigate(retour)}>← Revenir au travail</Button>
            <Button variant="quiet" onClick={() => void charger()}>
              Refaire une série
            </Button>
          </div>
        </Card>
      </Screen>
    );
  }

  const item = etat.serie.items[etat.index];
  if (item === undefined) {
    // Inatteignable : `index` ne dépasse jamais la longueur, l'avancement
    // passant par « finie ». Le repli évite qu'une série vide — que la route
    // refuse de servir — fasse tomber l'écran plutôt que de le fermer.
    return <Loading label="Je prépare la série…" />;
  }

  const dernier = etat.index >= etat.serie.items.length - 1;
  const avancer = () => {
    if (dernier) {
      setEtat({
        quoi: "finie",
        serie: etat.serie,
        ms: releve(chrono.current, performance.now()).ms,
      });
      return;
    }
    setEtat({ quoi: "encours", serie: etat.serie, index: etat.index + 1 });
  };

  return (
    <Screen title={titre}>
      {/* Le bandeau de la série : ce qu'on travaille, où l'on en est, et
          l'horloge. Au-dessus de l'exercice et jamais dedans — dans le panneau,
          il redeviendrait un compteur par question. */}
      <div className="serie__bandeau">
        <span className="serie__libelle">{etat.serie.libelle}</span>
        <span className="serie__rang">{avancement(etat.index, etat.serie.items.length)}</span>
        <span className="serie__chrono" aria-label="Temps écoulé depuis le début de la série">
          {chronoEnClair(ms)}
        </span>
      </div>
      <ExerciceEtCorrige
        key={item.exercise_id}
        item={item}
        matiere={matiere}
        retour={() => navigate(retour)}
        suivant={avancer}
        libelleRetour="← Arrêter la série"
        libelleSuivant={dernier ? "Terminer la série" : "Question suivante"}
        libelleChanger="Arrêter la série"
      />
    </Screen>
  );
}
